import { MoreVertical, Pencil } from 'lucide-react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { navigateToGameUrl } from '../../utils/lobbyMyGamesNavigation';
import { formatMyGamesStartDisplay } from '../../utils/lobbyGameDisplay';
import { getMyGamesSportLogoPath } from '../../utils/myGamesSportLogo';
import {
  formatSurvivorFieldCount,
  isSurvivorContestComplete,
  resolveSurvivorCurrentWeek,
  survivorEntryLabel,
  type SurvivorMyGame,
} from '../../utils/lobbyMyGamesSurvivor';

interface LobbySurvivorCardProps {
  game: SurvivorMyGame;
  /** Full public URL with token appended (or null if unavailable). */
  publicUrl: string | null;
}

export default function LobbySurvivorCard({ game, publicUrl }: LobbySurvivorCardProps) {
  const isComplete = isSurvivorContestComplete(game);
  const currentWeek = resolveSurvivorCurrentWeek(game);
  const status = survivorEntryLabel(game);
  const logoPath = getMyGamesSportLogoPath(game.sport ?? undefined);

  const startDisplay = formatMyGamesStartDisplay(game.startUtc);

  const hasCurrentWeekPick = currentWeek
    ? game.picks.some(pick => pick.weekNumber === currentWeek.weekNumber)
    : false;

  // Eliminated entrants cannot pick again — the tile stays tappable so they can still open the
  // contest to watch, but a Make/Edit CTA would imply they can act.
  const isEliminated = game.entry.status === 'eliminated';
  const showPickCta = !isComplete && !isEliminated && currentWeek?.status === 'open' && !!publicUrl;

  const statusTextClass =
    status.tone === 'out'
      ? 'text-[#ff6b6b]'
      : status.tone === 'alive'
        ? 'text-[#53d337]'
        : 'text-[#ABABAB]';

  const handleNavigate = (e?: MouseEvent | KeyboardEvent) => {
    if (!publicUrl) return;
    e?.preventDefault();
    navigateToGameUrl(publicUrl);
  };

  return (
    <a
      href={publicUrl ?? undefined}
      role={publicUrl ? 'link' : undefined}
      tabIndex={publicUrl ? 0 : -1}
      aria-disabled={!publicUrl}
      data-testid={`my-games-survivor-${game.pickemId}`}
      onClick={handleNavigate}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') handleNavigate(e);
      }}
      className={`block rounded-xl p-4 border border-[#373737] bg-[#242424] transition-all duration-200 ${
        publicUrl
          ? 'hover:ring-2 hover:ring-[#53d337]/30 cursor-pointer active:scale-[0.98]'
          : 'cursor-default pointer-events-none'
      }`}
    >
      {/* Row 1: Logo + Title + Menu */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden bg-[#373737] flex items-center justify-center">
            {logoPath ? (
              <img
                src={logoPath}
                alt={`${game.sport ?? ''} logo`}
                className="w-full h-full object-contain p-1"
              />
            ) : (
              <span className="text-zinc-400 font-medium text-[10px]">Logo</span>
            )}
          </div>
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="font-semibold text-sm text-white tracking-wide uppercase leading-tight">
              {game.title}
            </span>
            <div className="flex items-center gap-1.5 mt-0.5 whitespace-nowrap">
              <span className="text-zinc-500 text-sm font-normal">Start:</span>
              <span className="text-[#53d337] font-semibold text-sm">{startDisplay}</span>
            </div>
          </div>
        </div>
        <MoreVertical className="w-5 h-5 text-zinc-400 flex-shrink-0" />
      </div>

      {/* Divider */}
      <div className="border-t-2 border-[#373737]/60 mb-4" />

      {/* Two-up stat row — matches Pick'em / Squares My Games cards. */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1 text-left border-r border-[#6F6F6F] pr-4">
          <span className="text-xs text-zinc-400">Survived</span>
          <span
            data-testid={`my-games-survivor-field-${game.pickemId}`}
            className="text-sm font-bold text-white"
          >
            {formatSurvivorFieldCount(game)}
          </span>
        </div>
        <div className="flex flex-col gap-1 text-right">
          <span className="text-xs text-zinc-400">My Status</span>
          <span
            data-testid={`my-games-survivor-status-${game.pickemId}`}
            className={`text-sm font-bold ${statusTextClass}`}
          >
            {status.text}
          </span>
        </div>
      </div>

      {/* Make/Edit this week's pick — only while a week is open and the entrant can still act. */}
      {showPickCta && currentWeek && (
        <div className="mt-4 flex justify-center">
          {hasCurrentWeekPick ? (
            <span className="w-[152px] h-[32px] rounded-full border border-[#373737] bg-[#121212] text-white flex items-center justify-center gap-2 transition-colors hover:border-[#53d337]">
              <span className="text-[13px] font-semibold">Edit Pick</span>
              <Pencil className="w-4 h-4" />
            </span>
          ) : (
            <span className="min-w-[152px] h-[32px] px-4 rounded-full bg-[#E9344A] text-white flex items-center justify-center transition-colors hover:bg-[#d12a3e]">
              <span className="text-[13px] font-semibold">
                Make Week {currentWeek.weekNumber} Pick
              </span>
            </span>
          )}
        </div>
      )}
    </a>
  );
}
